// ユーザー管理関連の型定義
// Settings画面のユーザー管理タブで使用

import type { UserRole as UserRoleType } from './settings';
import {
  type LegacyUserStatus,
  UserStatusEnum,
  toUserStatus,
  toLegacyUserStatus,
} from './enums';

export type UserRole = UserRoleType;

/**
 * ユーザーステータス型（小文字版 - レガシー互換）
 * Issue #1780: 新規コードではUserStatusEnumを使用してください
 * @see UserStatusEnum
 */
export type UserStatus = LegacyUserStatus;

// Re-export for convenience
export { UserStatusEnum, toUserStatus, toLegacyUserStatus };

export interface UserInfo {
  id: string;
  basicInfo: {
    firstName: string;
    lastName: string;
    email: string;
    avatar?: string;
  };
  access: {
    role: UserRole;
    status: UserStatus;
    permissions: UserPermissions;
    joinDate: Date;
    lastLogin?: Date;
  };
  invitation?: {
    invitedBy: string;
    invitedAt: Date;
    acceptedAt?: Date;
    invitationToken?: string;
  };
  settings: {
    emailNotifications: boolean;
    pushNotifications: boolean;
    language: string;
    timezone: string;
  };
  createdAt: Date;
  updatedAt: Date;
  updatedBy: string;
}

export interface UserPermissions {
  // 顧客管理
  canViewCustomers: boolean;
  canEditCustomers: boolean;
  canDeleteCustomers: boolean;
  
  // 予約管理
  canViewReservations: boolean;
  canCreateReservations: boolean;
  canEditReservations: boolean;
  canCancelReservations: boolean;
  
  // スタッフ管理
  canViewStaff: boolean;
  canEditStaff: boolean;
  canManageStaff: boolean;
  
  // 店舗設定
  canViewStoreSettings: boolean;
  canEditStoreSettings: boolean;
  
  // ユーザー管理
  canViewUsers: boolean;
  canManageUsers: boolean;
  canInviteUsers: boolean;
  
  // 財務・レポート
  canViewReports: boolean;
  canViewFinancials: boolean;
  canExportData: boolean;
  
  // システム設定
  canAccessSettings: boolean;
  canManageIntegrations: boolean;
}

// デフォルト権限設定
export const DEFAULT_PERMISSIONS: Record<UserRole, UserPermissions> = {
  owner: {
    canViewCustomers: true,
    canEditCustomers: true,
    canDeleteCustomers: true,
    canViewReservations: true,
    canCreateReservations: true,
    canEditReservations: true,
    canCancelReservations: true,
    canViewStaff: true,
    canEditStaff: true,
    canManageStaff: true,
    canViewStoreSettings: true,
    canEditStoreSettings: true,
    canViewUsers: true,
    canManageUsers: true,
    canInviteUsers: true,
    canViewReports: true,
    canViewFinancials: true,
    canExportData: true,
    canAccessSettings: true,
    canManageIntegrations: true,
  },
  manager: {
    canViewCustomers: true,
    canEditCustomers: true,
    canDeleteCustomers: true,
    canViewReservations: true,
    canCreateReservations: true,
    canEditReservations: true,
    canCancelReservations: true,
    canViewStaff: true,
    canEditStaff: true,
    canManageStaff: false,
    canViewStoreSettings: true,
    canEditStoreSettings: true,
    canViewUsers: true,
    canManageUsers: false,
    canInviteUsers: false,
    canViewReports: true,
    canViewFinancials: true,
    canExportData: true,
    canAccessSettings: true,
    canManageIntegrations: false,
  },
  staff: {
    canViewCustomers: true,
    canEditCustomers: true,
    canDeleteCustomers: false,
    canViewReservations: true,
    canCreateReservations: true,
    canEditReservations: true,
    canCancelReservations: false,
    canViewStaff: true,
    canEditStaff: false,
    canManageStaff: false,
    canViewStoreSettings: true,
    canEditStoreSettings: false,
    canViewUsers: false,
    canManageUsers: false,
    canInviteUsers: false,
    canViewReports: false,
    canViewFinancials: false,
    canExportData: false,
    canAccessSettings: false,
    canManageIntegrations: false,
  },
  receptionist: {
    canViewCustomers: true,
    canEditCustomers: false,
    canDeleteCustomers: false,
    canViewReservations: true,
    canCreateReservations: true,
    canEditReservations: true,
    canCancelReservations: false,
    canViewStaff: false,
    canEditStaff: false,
    canManageStaff: false,
    canViewStoreSettings: false,
    canEditStoreSettings: false,
    canViewUsers: false,
    canManageUsers: false,
    canInviteUsers: false,
    canViewReports: false,
    canViewFinancials: false,
    canExportData: false,
    canAccessSettings: false,
    canManageIntegrations: false,
  },
};

// ユーザー招待関連
export interface UserInvitation {
  id: string;
  email: string;
  role: UserRole;
  permissions: UserPermissions;
  invitedBy: string;
  invitedAt: Date;
  expiresAt: Date;
  token: string;
  status: 'pending' | 'accepted' | 'expired' | 'cancelled';
  message?: string;
}

// ユーザー統計
export interface UserStats {
  total: number;
  byRole: Record<UserRole, number>;
  byStatus: Record<UserStatus, number>;
  activeUsers: number;
  pendingInvitations: number;
  lastWeekLogins: number;
}

// バリデーション結果
export interface UserValidationResult {
  isValid: boolean;
  errors: {
    field: string;
    message: string;
  }[];
}

// フィルター・検索用
export interface UserFilterOptions {
  role?: UserRole;
  status?: UserStatus;
  permissions?: Partial<UserPermissions>;
  lastLogin?: {
    from?: Date;
    to?: Date;
  };
}

// バルク操作用
export interface UserBulkOperation {
  type: 'status_change' | 'role_change' | 'permission_update' | 'delete';
  userIds: string[];
  data?: {
    status?: UserStatus;
    role?: UserRole;
    permissions?: Partial<UserPermissions>;
  };
}

export default UserInfo;